import { morph } from '@theme/morph'

const SectionRendererSettings = {
  sectionIdPrefix: 'shopify-section-'
}

class SectionRenderer {
  #cache = new Map()
  #abortControllersBySectionId = new Map()
  #pendingPromises = new Map()

  constructor() {
    window.addEventListener('load', this.#cachePageSections.bind(this))
  }

  async renderSection(sectionId, options) {
    const { cache = !Shopify.designMode } = options ?? {}

    this.#abortPendingMorph(sectionId)

    const abortController = new AbortController()
    this.#abortControllersBySectionId.set(sectionId, abortController)

    const sectionHTML = await this.getSectionHTML(sectionId, cache)

    if (!abortController.signal.aborted) {
      this.#abortControllersBySectionId.delete(sectionId)
      morphSection(sectionId, sectionHTML)
    }

    return sectionHTML
  }

  #abortPendingMorph(sectionId) {
    const existingAbortController =
      this.#abortControllersBySectionId.get(sectionId)
    if (existingAbortController) {
      existingAbortController.abort()
    }
  }

  async getSectionHTML(
    sectionId,
    useCache = true,
    url = new URL(window.location.href)
  ) {
    const sectionUrl = buildSectionRenderingURL(sectionId, url)

    let pendingPromise = this.#pendingPromises.get(sectionUrl)
    if (pendingPromise) {
      return pendingPromise
    }

    if (useCache) {
      const cachedHTML = this.#cache.get(sectionUrl)
      if (cachedHTML) {
        return cachedHTML
      }
    }

    pendingPromise = fetch(sectionUrl).then((response) => response.text())
    this.#pendingPromises.set(sectionUrl, pendingPromise)

    const sectionHTML = await pendingPromise
    this.#pendingPromises.delete(sectionUrl)

    this.#cache.set(sectionUrl, sectionHTML)
    return sectionHTML
  }

  #cachePageSections() {
    for (const section of document.querySelectorAll('.shopify-section')) {
      const url = buildSectionRenderingURL(section.id)
      if (this.#cache.get(url)) {
        return
      }
      if (containsShadowRoot(section)) {
        return
      }

      this.#cache.set(url, section.outerHTML)
    }
  }
}

function buildSectionRenderingURL(
  sectionId,
  url = new URL(window.location.href)
) {
  url.searchParams.set('section_id', normalizeSectionId(sectionId))
  url.searchParams.sort()
  return url.toString()
}

export function buildSectionSelector(sectionId) {
  return `${SectionRendererSettings.sectionIdPrefix}${sectionId}`
}

export function normalizeSectionId(sectionId) {
  return sectionId.replace(
    new RegExp(`^${SectionRendererSettings.sectionIdPrefix}`),
    ''
  )
}

function containsShadowRoot(element) {
  return (
    !!element.shadowRoot ||
    Array.from(element.children).some(containsShadowRoot)
  )
}

export async function morphSection(sectionId, html) {
  const fragment = new DOMParser().parseFromString(html, 'text/html')
  const existingElement = document.getElementById(
    buildSectionSelector(sectionId)
  )
  const newElement = fragment.getElementById(buildSectionSelector(sectionId))

  if (!existingElement) {
    throw new Error(`Section ${sectionId} not found`)
  }

  if (!newElement) {
    throw new Error(
      `Section ${sectionId} not found in the section rendering response`
    )
  }

  const openCartDrawerIds = Array.from(
    existingElement.querySelectorAll('drawer-cart')
  )
    .filter((el) => {
      if (!(el instanceof HTMLElement)) {
        return false
      }
      const dialogEl = el.querySelector('dialog.drawer-dialog')
      const isOpen =
        dialogEl instanceof HTMLDialogElement
          ? dialogEl.open
          : el.dataset.nav === 'open'
      return Boolean(isOpen)
    })
    .map((el) => el.id)
    .filter((id) => typeof id === 'string' && id.length > 0)

  morph(existingElement, newElement)

  for (const id of openCartDrawerIds) {
    const drawer = document.getElementById(id)
    try {
      drawer?.open?.()
    } catch {
      /* empty */
    }
  }
}

export const sectionRenderer = new SectionRenderer()
